import { Fragment } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { parentOf, type TrailItem } from "./entityTrail";

/**
 * Die Leiste über einer Entitätsseite: „← Zurück zu …" und der Pfad Projekt › Gebäude › Etage › Raum.
 *
 * Vorher gab es zwischen den Ebenen keinen Weg nach oben außer der Seitenleiste und dem
 * Browser-Zurück; wer schließen wollte, griff zum Öffnen-Symbol und landete auf „Entity not found".
 * Zurück führt zum echten Elternteil, nie in die Browser-Historie, damit es auch nach einem
 * direkt geöffneten Link stimmt.
 */
export function EntityHierarchyBar({ trail }: { trail: TrailItem[] }) {
  const { t } = useTranslation();
  if (trail.length < 2) return null;
  const parent = parentOf(trail);

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1" data-testid="entity-hierarchy-bar">
      {parent?.href ? (
        // `!text-…`: Das Theme färbt jedes `a` in der Sekundärfarbe; die Leiste soll ruhig bleiben.
        <Button asChild variant="ghost" size="sm" className="-ml-2 h-8 gap-1.5 px-2 !text-foreground">
          <Link to={parent.href}>
            <ArrowLeft aria-hidden="true" className="h-4 w-4" />
            {t("entityPage.backTo", { name: parent.label })}
          </Link>
        </Button>
      ) : null}
      <Breadcrumb aria-label={t("entityPage.breadcrumb")} className="min-w-0">
        <BreadcrumbList className="gap-1.5 text-xs sm:gap-1.5 sm:text-sm">
          {trail.map((item, index) => (
            <Fragment key={`${item.type}-${index}`}>
              {index > 0 ? <BreadcrumbSeparator /> : null}
              <BreadcrumbItem className="min-w-0">
                {index === trail.length - 1 ? (
                  // Kein `BreadcrumbPage`: dessen `role="link"` bekäme vom Theme die Linkfarbe.
                  <span aria-current="page" className="font-medium text-foreground">
                    {item.label}
                  </span>
                ) : !item.href ? (
                  <span className="text-muted-foreground">{item.label}</span>
                ) : (
                  <Link
                    to={item.href}
                    className="!text-muted-foreground transition-colors hover:!text-foreground hover:underline"
                    title={t(`entityPage.types.${item.type}`)}
                  >
                    {item.label}
                  </Link>
                )}
              </BreadcrumbItem>
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
    </div>
  );
}

/** Die Art einer Entität als kleine Marke vor ihrem Namen („Etage", „Raum"). */
export function EntityTypePill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium text-muted-foreground">
      {children}
    </span>
  );
}

export default EntityHierarchyBar;
